import { ChangeDetectorRef, Inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ComponentStore, OnStoreInit } from '@ngrx/component-store';import { tapResponse } from '@ngrx/operators';

import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable, tap, switchMap, pipe, first, of, iif, finalize } from "rxjs";
import { LeadRawValue } from "src/app/core/models/lead/lead-raw-value.model";
import { Lead } from "src/app/core/models/lead/lead.model";
import { APP_CONFIG } from "src/app/shared/configurations/configurations.factory";
import { Configurations } from "src/app/shared/configurations/models/configurations.model";
import { LeadReceivedInfoDialogComponent } from "../../dialogs/lead-received-info-dialog/lead-received-info-dialog.component";
import { GlobalSpinnerActions } from "../../global-spinner/actions-types";
import { SnackbarNotificationService } from "../../notifications/snackbar-notification/snackbar-notification.service";
import { LeadInfoService } from "./services/lead-info.service";
import { LobbyErrorHandlerService } from "src/app/core/services/lobby-error-handler.service";
import { LeadManagerAction } from "src/app/features/lead-manager/lead-manager.actions";
import { LeadActions } from "src/app/core/events/lead.actions";

export interface LeadInfoState {
    lead: Lead | null,
    receivedLeadInfo: LeadRawValue[] | undefined
}

const initialState: LeadInfoState = {
    lead: null,
    receivedLeadInfo: undefined
}

@Injectable()
export class SidenavContentLeadInfo extends ComponentStore<LeadInfoState> implements OnStoreInit {

    lead$ = this.select(state => state.lead)
    private receivedInfo$ = this.select(state => state.receivedLeadInfo)

    constructor(@Inject(APP_CONFIG) private config: Configurations,
        private readonly appStore: Store,
        actions$: Actions,
        private readonly leadInfoService: LeadInfoService,
        private readonly snackbarNotification: SnackbarNotificationService,
        private readonly translateService: TranslateService,
        private readonly dialog: MatDialog,
        private readonly cdr: ChangeDetectorRef,
        private readonly errorHandlerService: LobbyErrorHandlerService) {
        super();

        actions$.pipe(
            ofType(LeadActions.leadRequestedAutoAssign),
            // tap((action) => this.updateAutoOfferedPendingLeadIds(action.leadId))
        )
    }

    ngrxOnStoreInit() {
    }

    // effects

    readonly loadLeadInfo = this.effect((LeadId$: Observable<number>) => {
        return LeadId$.pipe(
            tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
            switchMap(id => this.leadInfoService.getLeadInfo$(id)),
            this.errorHandlerService.handleError,
            tapResponse((info) => {
                this.setLeadInfo(info)
                this.cdr.markForCheck()
                this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())
            },
                err => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())
            )
        )
    })

    readonly reloadLeadInfo = this.effect<void>(
        pipe(
            switchMap(() => this.lead$.pipe(first())),
            tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
            switchMap(lead => this.leadInfoService.getLeadInfo$(lead!.id)),
            this.errorHandlerService.handleError,
            tapResponse((info) => {
                this.setLeadInfo(info)
                this.cdr.markForCheck()
                this.translateService.get('messages.lead.refreshed').subscribe((message: string) => this.snackbarNotification.openInfo(message))
                this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())
            },
                err => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())
            )
        )
    )

    readonly showReceivedLeadInfoDialog = this.effect<void>(
        pipe(
            switchMap(() => this.receivedInfo$.pipe(first())),
            switchMap((receivedInfo) => iif(() => !!receivedInfo, of(receivedInfo!),
                this.lead$.pipe(
                    first(),
                    tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
                    switchMap(lead => this.leadInfoService.getReceivedLeadInfo$(lead!.id)),
                    finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner()))
                )
            )),
            tap(infos => {
                this.setReceivedInfo(infos)
                this.dialog.open(LeadReceivedInfoDialogComponent, { data: infos })
            }),
            this.errorHandlerService.handleError,
        )
    )

    // updaters

    readonly setLeadInfo = this.updater((state, lead: Lead) => ({
        ...initialState,
        lead: lead
    }))

    readonly offloadLeadInfo = this.setState(initialState)

    private readonly setReceivedInfo = this.updater((state, infos: LeadRawValue[]) => ({
        ...state,
        receivedLeadInfo: infos
    }))
}


